import { useState } from "react";
import {
  ScanLine,
  QrCode,
  Copy,
  Trash2,
  History as HistoryIcon,
  Link as LinkIcon,
  Type,
  Wifi,
  Mail,
  Phone,
  MessageSquare,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { clearHistory, removeHistoryItem } from "../lib/qrHistory";

const TYPE_ICON = {
  url: LinkIcon,
  text: Type,
  wifi: Wifi,
  email: Mail,
  phone: Phone,
  sms: MessageSquare,
};

const TYPE_LABEL = {
  url: "URL",
  text: "Metin",
  wifi: "Wi-Fi",
  email: "E-posta",
  phone: "Telefon",
  sms: "SMS",
};

export default function HistoryPanel({ items, onChange }) {
  const [confirmClear, setConfirmClear] = useState(false);

  const handleCopy = async (value) => {
    await navigator.clipboard.writeText(value);
    toast.success("Panoya kopyalandı");
  };

  const handleRemove = (id) => {
    const next = removeHistoryItem(id);
    onChange && onChange(next);
  };

  const handleClearAll = () => {
    if (!confirmClear) {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 2500);
      return;
    }
    clearHistory();
    onChange && onChange([]);
    setConfirmClear(false);
    toast.success("Geçmiş temizlendi");
  };

  return (
    <div
      data-testid="history-panel"
      className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col h-full"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
            <HistoryIcon className="w-4 h-4 text-blue-600" />
          </div>
          <h2 className="font-display font-bold text-slate-900">Geçmiş</h2>
          <span className="text-xs text-slate-400 font-medium">
            ({items.length})
          </span>
        </div>
        {items.length > 0 && (
          <button
            data-testid="history-clear-button"
            onClick={handleClearAll}
            className={`text-xs font-semibold rounded-full px-3 py-1.5 transition-all ${
              confirmClear
                ? "bg-red-600 text-white"
                : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            {confirmClear ? "Emin misiniz?" : "Temizle"}
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin">
        {items.length === 0 ? (
          <div
            data-testid="history-empty"
            className="flex flex-col items-center justify-center text-center px-6 py-14 text-slate-500"
          >
            <div className="w-14 h-14 rounded-2xl border-2 border-dashed border-slate-200 flex items-center justify-center mb-3">
              <HistoryIcon className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <p className="text-sm font-medium text-slate-600">
              Henüz işlem yapılmadı.
            </p>
            <p className="text-xs mt-1 text-slate-400 max-w-[220px]">
              Okuduğunuz ve oluşturduğunuz QR kodlar burada listelenir.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((item) => {
              const TypeIcon = TYPE_ICON[item.type] || Type;
              const KindIcon = item.kind === "scan" ? ScanLine : QrCode;
              return (
                <li
                  key={item.id}
                  data-testid={`history-item-${item.id}`}
                  className="px-5 py-3.5 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
                        item.kind === "scan"
                          ? "bg-emerald-50 text-emerald-600"
                          : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      <KindIcon className="w-4 h-4" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        <span
                          className={
                            item.kind === "scan"
                              ? "text-emerald-700"
                              : "text-blue-700"
                          }
                        >
                          {item.kind === "scan" ? "Okundu" : "Oluşturuldu"}
                        </span>
                        <span className="text-slate-300">•</span>
                        <span className="inline-flex items-center gap-1">
                          <TypeIcon className="w-3 h-3" />
                          {TYPE_LABEL[item.type] || "Metin"}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-slate-800 truncate font-medium">
                        {item.value}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {formatTime(item.createdAt)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Button
                        data-testid="history-item-copy-button"
                        onClick={() => handleCopy(item.value)}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                        aria-label="Kopyala"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid="history-item-remove-button"
                        onClick={() => handleRemove(item.id)}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                        aria-label="Sil"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function formatTime(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "az önce";
    if (mins < 60) return `${mins} dk önce`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} saat önce`;
    return d.toLocaleString("tr-TR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}
